// ============================================================================
// CalendarLegend — the shared, bilingual colour key for the merged calendar.
// ----------------------------------------------------------------------------
// Explains the PROVENANCE palette (ENTRY_STYLE) used wherever sessions and
// activities are merged onto one grid: coral = paid PT session, violet = coach
// homework, lime = the client's own logged exercise. Rendered on BOTH the
// trainer all-clients calendar (CalendarPage / dashboard) and the client's own
// calendar, so the two sides read the same colour language.
//
// Strings live in the `activity` namespace (legend.*) so they translate to
// 繁中 with no English leakage. Pure presentational — no data, no locked
// primitive restyle, mobile-first (wraps on phone).
// ============================================================================
import { useTranslation } from "react-i18next";
import type { EntryProvenance } from "./viewModel";
import { ENTRY_STYLE } from "./sessionStyle";
import { cn } from "../lib/cn";

/** The provenance entries shown in the legend, in reading order. */
const LEGEND_ITEMS: EntryProvenance[] = [
  "pt_session",
  "activity_suggested",
  "activity_own",
  "meal",
  "weekly_goal",
];

export interface CalendarLegendProps {
  className?: string;
  /** Render a heading above the swatches (default: visually-hidden label only). */
  showTitle?: boolean;
}

export function CalendarLegend({ className, showTitle = false }: CalendarLegendProps) {
  const { t } = useTranslation("activity");
  return (
    <div className={className}>
      {showTitle && (
        <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("legend.title")}
        </p>
      )}
      <ul
        className="flex flex-wrap items-center gap-x-4 gap-y-2"
        aria-label={t("legend.title")}
      >
        {LEGEND_ITEMS.map((kind) => (
          <li
            key={kind}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-body-soft"
          >
            <span
              className={cn("h-2.5 w-2.5 shrink-0 rounded-full", ENTRY_STYLE[kind].dot)}
              aria-hidden
            />
            {t(`provenance.${kind}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}
